export const UNKNOWN_VERSION = "Unknown";
export const UNASSIGNED_SCOPE = "Unassigned";

export interface VersionRow {
  version: string;
  count: number;
}

export interface DeviceRow {
  uuid: string;
  version: string;
  alias?: string;
  orgId?: string;
  orgName?: string;
}

export type ScopeUnit = "orgs" | "products";

export interface ScopeRow {
  scope: string;
  count: number;
}

export interface ScopeDistData {
  orgCounts: ScopeRow[];
  productCounts: ScopeRow[];
}

export const normalizeVersion = (version: string | undefined): string =>
  (version ?? "").trim() || UNKNOWN_VERSION;

export const aggregateByVersion = (devices: DeviceRow[]): VersionRow[] => {
  const counts = new Map<string, number>();
  for (const device of devices) {
    const v = normalizeVersion(device.version);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([version, count]) => ({ version, count }))
    .sort((a, b) => b.count - a.count || a.version.localeCompare(b.version));
};

export const toDeviceRows = (data: unknown): DeviceRow[] => {
  if (!Array.isArray(data)) return [];
  return data.map((d: Record<string, unknown>) => ({
    uuid: typeof d?.uuid === "string" ? d.uuid : "",
    version: typeof d?.version === "string" ? d.version : "",
    alias: typeof d?.alias === "string" ? d.alias : undefined,
    orgId: typeof d?.orgId === "string" ? d.orgId : undefined,
    orgName: typeof d?.orgName === "string" ? d.orgName : undefined,
  }));
};

export const toScopeRows = (data: unknown): ScopeRow[] => {
  if (!Array.isArray(data)) return [];
  return data
    .map((d: Record<string, unknown>) => ({
      scope: typeof d?.scope === "string" ? d.scope : "",
      count: typeof d?.count === "number" ? d.count : 0,
    }))
    .filter((r) => r.scope !== "");
};

export const toScopeDistData = (data: unknown): ScopeDistData => {
  const obj = (data ?? {}) as Record<string, unknown>;
  return {
    orgCounts: toScopeRows(obj.orgCounts),
    productCounts: toScopeRows(obj.productCounts),
  };
};
